import { lagoon } from '@uhg-lagoon/sdk';
import {
  fetchLagoonFallbackData,
  createLagoonFallbackInterface,
} from '@sde/widgets-core/src/tools/lagoon/helpers/lagoonFallbackData';

export class LagoonSDK {
  constructor(namespace, env, schema) {
    if (LagoonSDK._instance) {
      return LagoonSDK._instance;
    }

    this.namespace = namespace;
    this.env = env;
    this.schema = schema || {
      loadTables: true,
      loadForms: false,
    };
    this.isInitialized = false;
    this.featureFlags = {};
    this.uiMessages = {};
    this.errorMessages = {};
    this.payers = [];
    LagoonSDK._instance = this;
  }

  async init() {
    this.lagoon = lagoon;
    try {
      await lagoon.initialize(this.namespace, this.env, {
        ...this.schema,
        onReady: this.toggleOnReady(),
      });
      this.mapTables();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(':: Lagoon Initialize Error: ', e);

      // Try to fetch from CDN fallback
      try {
        const fallbackData = await fetchLagoonFallbackData(
          `${process.env.LAGOON_FALLBACK_CDN_URL}lagoon-${this.env}/lagoon_sde_backup.json`
        );
        this.lagoon = createLagoonFallbackInterface(
          fallbackData,
          this.namespace
        );
        this.isInitialized = true;
        this.mapTables();
        console.log(':: Lagoon fallback data loaded successfully');
      } catch (fallbackError) {
        console.error(':: Lagoon fallback failed:', fallbackError);
        throw e;
      }
    }
    return this;
  }

  mapTables() {
    this.mapFeatureFlags();
    this.mapErrorMessages();
    this.mapUIMessages();
    this.mapPayers();
  }

  toggleOnReady() {
    this.isInitialized = true;
  }

  mapFeatureFlags() {
    this.lagoon
      .getTable('sde-ppd.feature-flags', null, this.env)
      .getRow()
      .forEach((feature) => {
        const { key, active } = feature;
        this.featureFlags[key] = active;
      });
  }

  mapUIMessages() {
    this.lagoon
      .getTable('sde-ppd.ui-messages', null, this.env)
      .getRow()
      .forEach((uiMessage) => {
        const { messageId, message } = uiMessage;
        this.uiMessages[messageId] = message;
      });
  }

  mapErrorMessages() {
    this.lagoon
      .getTable('sde-ppd.error-messages', null, this.env)
      .getRow()
      .forEach((errorMessage) => {
        const { messageId, message } = errorMessage;
        this.errorMessages[messageId] = message;
      });
  }

  async mapPayers() {
    const newPayers = [];
    await this.lagoon
      .getTable('sde-ppd.payers', null, this.env)
      .getRow()
      .forEach((payer) => {
        const payerCopy = payer;
        const { applications } = payerCopy;
        // some applications are an empty string
        if (!applications || applications.length === 0) {
          payerCopy.applications = [];
        } else {
          // if the applications is already an array return the array, else split the string on new line
          const splitApplications = Array.isArray(applications)
            ? applications
            : applications.split('\n');
          payerCopy.applications = splitApplications;
        }
        newPayers.push(payerCopy);
      });
    this.payers = newPayers;
  }

  async getTable(tableName, keyPath = null) {
    this[tableName] = this.lagoon.getTable(tableName, keyPath, this.env);
    return this[tableName];
  }

  getRow(tableName, rowKey, keyPath = null) {
    return this.lagoon.getTable(tableName, keyPath, this.env).getRow(rowKey);
  }
}
